import React, { useState } from "react";

import { RiCloseLine, RiEdit2Line, RiEye2Line } from "@remixicon/react";
import { PermissionAction, PermissionAssignment } from "../../../../__generated__/api";
import Agents from "../../../pickers/agents/Agents";
import Popover from "../../../popover/Popover";
import Button from "../../../theme/button/Button";
import Toggle from "../../../theme/toggle/Toggle";

const GROUP_EVERYONE = "urn:fq:user-group:everyone";

export default function ShareOptionsSettings({
    value,
    onChange,
    shareTypeLabel,
    lowerPermission,
}: {
    value: PermissionAssignment[];
    onChange(value: PermissionAssignment[]): void;
    shareTypeLabel: string;
    lowerPermission?: PermissionAction
}) {
    const [hasSelectedCustom, setHasSelectedCustom] = useState(false);

    const assignees = value?.mapFilter(row => row.assigneeId).filterNull() ?? [];

    const permission = lowerPermission ?? PermissionAction.Read;

    const readUsers = value
        .filter(row => row.action === permission && row.assigneeId && row.assigneeId !== GROUP_EVERYONE)
        .toIdList("assigneeId");

    const modifyUsers = value
        .filter(row => row.action === PermissionAction.Modify && row.assigneeId && row.assigneeId !== GROUP_EVERYONE)
        .toIdList("assigneeId");

    const isCustom = hasSelectedCustom || (assignees.isNotEmpty && !assignees.some(row => row === GROUP_EVERYONE));
    return (
        <div className="d-flex flex-column gap3-1 text-body-2-medium">
            <Toggle
                variant="radio"
                checked={!hasSelectedCustom && assignees.isEmpty}
                onChange={() => {
                    onChange([
                        {
                            action: PermissionAction.Modify,
                        },
                    ]);
                    setHasSelectedCustom(false);
                }}
                className="m3e-1"
                text="Personal"
            />
            <Toggle
                variant="radio"
                checked={!hasSelectedCustom && assignees.some(row => row === GROUP_EVERYONE)}
                onChange={() => {
                    onChange([{
                        assigneeId: GROUP_EVERYONE,
                        action: permission,
                    }]);
                    setHasSelectedCustom(false);
                }}
                className="m3e-1"
                text="Global"
            />
            <Toggle
                variant="radio"
                checked={isCustom}
                onChange={() => setHasSelectedCustom(true)}
                className="m3e-1"
                text={(
                    <span>
                        Custom
                        {" "}
                        <Popover size="sm" iconSize="xs" buttonClassName="help-icon mt-n1">
                            Manually customize which users or user groups can view or modify the
                            {" "}
                            {shareTypeLabel}
                        </Popover>

                    </span>
                )}
            />
            {isCustom && (
                <div>
                    <div className="m3t-1 d-flex align-items-center">
                        <Agents
                            onSelected={memberIds => {
                                const current = value.filter(row => row.action !== permission && row.assigneeId);
                                onChange(current.concat(memberIds?.map(assigneeId => ({
                                    assigneeId,
                                    action: permission,
                                })) ?? []));
                            }}
                            value={readUsers}
                            isMulti
                            isClearable
                            shouldIncludeGroups
                            shouldShowDropdown
                            className="flex-grow-1"
                            toggleText="View"
                            searchMinimum={5}
                            icon={<RiEye2Line />}
                        />

                        <Button
                            prefixIcon={<RiCloseLine />}
                            size="small"
                            variant="none"
                            onClick={() => {
                                onChange(value.filter(row => row.action !== permission && row.assigneeId));
                            }}
                            className={readUsers.isNotEmpty ? "visible" : "invisible"}
                        />
                    </div>
                    <div className="m3t-1 d-flex align-items-center">
                        <Agents
                            onSelected={memberIds => {
                                const current = value.filter(row => row.action !== PermissionAction.Modify && row.assigneeId);
                                onChange(current.concat(memberIds?.map(assigneeId => ({
                                    assigneeId,
                                    action: PermissionAction.Modify,
                                })) ?? []));
                            }}
                            isClearable
                            value={modifyUsers}
                            isMulti
                            shouldIncludeGroups
                            shouldShowDropdown
                            className="flex-grow-1"
                            toggleText="Modify"
                            searchMinimum={5}
                            icon={<RiEdit2Line />}
                        />
                        <Button
                            prefixIcon={<RiCloseLine />}
                            size="small"
                            variant="none"
                            onClick={() => {
                                onChange(value.filter(row => row.action !== PermissionAction.Modify && row.assigneeId));
                            }}
                            className={modifyUsers.isNotEmpty ? "visible" : "invisible"}
                        />
                    </div>

                </div>
            )}

        </div>
    );
}

ShareOptionsSettings.defaultProps = {
    lowerPermission: PermissionAction.Read,
};
